<template>
  <div class="d-flex flex-column pa-3" style="gap: 10px;">
    <template v-for="entry in schemaEntries">
      <!-- Pair of _from/_to fields → range slider -->
      <a-pricing-strategy-rule-type-condition-field-range-slider
        v-if="entry.type === 'range'"
        :key="entry.fromKey"
        :value="[
          cond.settings[entry.fromKey] || entry.min,
          cond.settings[entry.toKey] || entry.max
        ]"
        :label="fieldLabel(entry.fromKey)"
        :min="entry.min"
        :max="entry.max"
        :unit="entry.unit"
        @input="onRange(entry, $event)"
      />
      <!-- Posición en el compset → mismo selector de barritas que la acción -->
      <a-pricing-strategy-rule-type-condition-field-compset
        v-else-if="entry.type === 'compset'"
        :key="entry.key"
        :value="cond.settings[entry.key]"
        :values="entry.values"
        :label="fieldLabel(entry.key)"
        @input="cond.settings[entry.key] = $event"
      />
      <!-- Enum field → select -->
      <a-pricing-strategy-rule-type-condition-field-select
        v-else-if="entry.type === 'select'"
        :key="entry.key"
        :value="cond.settings[entry.key]"
        :items="entry.items"
        :label="fieldLabel(entry.key)"
        @input="cond.settings[entry.key] = $event"
      />
      <!-- Free number (currency / wide ranges) → text field -->
      <a-pricing-strategy-rule-type-condition-field-number
        v-else-if="entry.type === 'number'"
        :key="entry.key"
        :value="cond.settings[entry.key]"
        :min="entry.min"
        :max="entry.max"
        :unit="entry.unit"
        :readonly="readonly"
        :label="fieldLabel(entry.key)"
        @input="cond.settings[entry.key] = $event"
      />
      <!-- Numeric range → slider -->
      <a-pricing-strategy-rule-type-condition-field-slider
        v-else-if="entry.type === 'slider'"
        :key="entry.key"
        :value="cond.settings[entry.key] || entry.min"
        :min="entry.min"
        :max="entry.max"
        :unit="entry.unit"
        :label="fieldLabel(entry.key)"
        @input="cond.settings[entry.key] = $event"
      />
    </template>
  </div>
</template>

<script>
import { conditionSummaryFields } from "./ruleTypeRegistry.js";
import aPricingStrategyRuleTypeConditionFieldCompset from "./fields/aPricingStrategyRuleTypeConditionFieldCompset.vue";
import aPricingStrategyRuleTypeConditionFieldNumber from "./fields/aPricingStrategyRuleTypeConditionFieldNumber.vue";
import aPricingStrategyRuleTypeConditionFieldRangeSlider from "./fields/aPricingStrategyRuleTypeConditionFieldRangeSlider.vue";
import aPricingStrategyRuleTypeConditionFieldSelect from "./fields/aPricingStrategyRuleTypeConditionFieldSelect.vue";
import aPricingStrategyRuleTypeConditionFieldSlider from "./fields/aPricingStrategyRuleTypeConditionFieldSlider.vue";

// Reglas del schema que piden un campo numérico libre en vez de un slider
const NUMBER_RULES = ["currency", "number"];

// Un settings_schema mezcla strings ("required", "numeric") con objetos
// ({min, max} o {in: [...]}). El operador `in` solo acepta objetos, así que
// hay que descartar los strings antes de buscar la clave.
function findRuleWith(rules, key) {
  return (rules || []).find(r => r && typeof r === "object" && key in r);
}

export default {
  name: "a-pricing-strategy-rule-type-condition-body-generic",
  components: {
    aPricingStrategyRuleTypeConditionFieldCompset,
    aPricingStrategyRuleTypeConditionFieldNumber,
    aPricingStrategyRuleTypeConditionFieldSlider,
    aPricingStrategyRuleTypeConditionFieldRangeSlider,
    aPricingStrategyRuleTypeConditionFieldSelect
  },

  props: {
    cond: { type: Object, required: true },
    ruleType: { type: Object, required: true },
    readonly: { type: Boolean, default: false }
  },

  computed: {
    // La unidad del tipo (%, €, RN…) ya la conoce el registry, que la usa para
    // la barra de resumen: se reutiliza en vez de inventar otra heurística.
    schemaUnit() {
      const descriptor = conditionSummaryFields(
        this.ruleType.code,
        this.ruleType.settings_schema
      );
      return (descriptor && descriptor.unit) || "";
    },
    schemaEntries() {
      const schema = this.ruleType.settings_schema || {};
      const keys = Object.keys(schema);
      const entries = [];
      const consumed = new Set();

      const numberEntry = key => {
        const rules = schema[key] || [];
        const minRule = findRuleWith(rules, "min");
        const maxRule = findRuleWith(rules, "max");
        return {
          type: "number",
          key,
          min: minRule ? minRule.min : 0,
          max: maxRule ? maxRule.max : 9999,
          unit: rules.includes("currency") ? "€" : ""
        };
      };

      keys.forEach(key => {
        if (consumed.has(key)) return;

        const rules = schema[key] || [];
        const isNumberField = NUMBER_RULES.some(r => rules.includes(r));

        // Check for _from/_to pair
        if (key.endsWith("_from")) {
          const toKey = key.replace(/_from$/, "_to");
          if (schema[toKey]) {
            consumed.add(key);
            consumed.add(toKey);
            // Un par de importes (p.ej. price_from/price_to) se pinta como dos
            // campos numéricos: un range slider de 0 a 9999 sería inservible.
            if (isNumberField) {
              entries.push(numberEntry(key), numberEntry(toKey));
              return;
            }
            const fromRules = schema[key] || [];
            const minRule = findRuleWith(fromRules, "min");
            const maxRule = findRuleWith(fromRules, "max");
            entries.push({
              type: "range",
              fromKey: key,
              toKey,
              unit: this.schemaUnit,
              min: minRule ? minRule.min : 0,
              max: maxRule ? maxRule.max : 100
            });
            return;
          }
        }

        consumed.add(key);
        const inRule = findRuleWith(rules, "in");
        const minRule = findRuleWith(rules, "min");
        const maxRule = findRuleWith(rules, "max");

        if (inRule && inRule.in) {
          // El rango de compset tiene su propio control con barritas, el
          // mismo que usa la acción: un desplegable con "highest" no dice nada.
          entries.push(
            key.endsWith("compset_range")
              ? { type: "compset", key, values: inRule.in }
              : {
                  type: "select",
                  key,
                  items: inRule.in.map(v => ({
                    text: this.fieldValueLabel(key, v),
                    value: v
                  }))
                }
          );
        } else if (isNumberField) {
          entries.push(numberEntry(key));
        } else if (minRule || maxRule) {
          entries.push({
            type: "slider",
            key,
            unit: this.schemaUnit,
            min: minRule ? minRule.min : 0,
            max: maxRule ? maxRule.max : 100
          });
        }
        // Other rule types (required-only) are skipped — no UI needed
      });

      return entries;
    }
  },

  methods: {
    // Etiqueta traducida del campo; si no hay traducción, la clave cruda
    // (que es lo que se mostraba antes para todos los tipos).
    //
    // Se mira primero una clave con el tipo delante, porque dos tipos pueden
    // compartir nombre de campo y querer decir cosas distintas: `price` es
    // "importe menor o igual que" en price-lower-than y "mayor o igual que" en
    // price-greater-than. El orden `<code>_field_<key>` y no
    // `field_<code>_<key>` evita chocar con fieldValueLabel.
    fieldLabel(key) {
      const typeKey = `pricing_strategy_rule_type_${this.ruleType.code}_field_${key}`;
      if (this.$te(typeKey)) return this.$t(typeKey);
      const i18nKey = `pricing_strategy_rule_type_field_${key}`;
      return this.$te(i18nKey) ? this.$t(i18nKey) : key;
    },
    fieldValueLabel(key, value) {
      const i18nKey = `pricing_strategy_rule_type_field_${key}_${value}`;
      return this.$te(i18nKey) ? this.$t(i18nKey) : value;
    },
    onRange(entry, v) {
      this.cond.settings[entry.fromKey] = v[0];
      this.cond.settings[entry.toKey] = v[1];
    }
  }
};
</script>
